import "./style.css";
import { ReactNode, useCallback, useEffect, useState, useRef } from "react";

// import CloseIcon from '@/assets/icons/close-dark.svg';
import CloseIcon from '@/assets/images/dashboard/add-bg-grn.svg'
import { getTooltipPosition, tooltipPositions } from "./helpers";
import { useAppDispatch, useAppSelector } from "@/shared/hooks/store";
import { selectHelpersShown } from "@/store/slices/auth/selectors";
import { addHelperShown } from "@/store/slices/auth";

interface IProps {
    open?: boolean;
    anchorEl?: HTMLElement | null;
    content?: ReactNode;
    onClose?(): void;
    hasCloseIcon?: boolean;
    helperArrowClassName?: string;
    uniqueIdentifier?: string;
    helperTimeout?: number;
    wrapperClassName?: string;
    className?: string;
    supportClickOutside?: boolean;
    forceBelow?: boolean;
    children?: ReactNode;
    customPosition?: keyof typeof tooltipPositions;
    squareColor?: string;
    squareBorderColor?: string;
};

export const Tooltip = ({
    className = '',
    wrapperClassName = '',
    helperTimeout = 6000,
    uniqueIdentifier,
    open,
    content,
    anchorEl,
    onClose,
    children,
    customPosition,
    supportClickOutside = true,
    forceBelow = false,
    hasCloseIcon = true,
    squareColor,
    squareBorderColor,

}: IProps) => {
    const [elemBounding, setElemBounding] = useState<DOMRect | null>(null);
    const toolTipRef = useRef<HTMLDivElement | null>(null);
    const helpersShown = useAppSelector(selectHelpersShown);
    const dispatch = useAppDispatch();
    const [width, setWidth] = useState<number | undefined>(undefined);
    const [right, setRight] = useState<number | undefined>(undefined);
    const initialized = useRef(false);
    const [alreadyShown, setAlreadyShown] = useState(uniqueIdentifier && helpersShown.includes(uniqueIdentifier));

    const updateBounding = useCallback(() => {
        if (anchorEl) {
            setElemBounding(anchorEl.getBoundingClientRect());
        }
    }, [anchorEl]);

    useEffect(() => {
        if (!anchorEl || !open) return;

        updateBounding();

        window.addEventListener('scroll', updateBounding, true);
        window.addEventListener('resize', updateBounding);

        const observer = new MutationObserver(updateBounding);
        observer.observe(anchorEl, {
            attributes: true,
            childList: true,
            subtree: true,
        });

        return () => {
            window.removeEventListener('scroll', updateBounding, true);
            window.removeEventListener('resize', updateBounding);
            observer.disconnect();
        };
    }, [anchorEl, updateBounding, open]);

    const close = () => {
        uniqueIdentifier && setAlreadyShown(true);
        onClose?.();
    }

    useEffect(() => {
        supportClickOutside && open && close && setTimeout(() => window.addEventListener('click', close), 0);
        return () => {
            supportClickOutside && close && window.removeEventListener('click', close);
        }
    }, [open]);

    useEffect(() => {
        if (open && uniqueIdentifier && !alreadyShown) {
            dispatch(addHelperShown(uniqueIdentifier));

            const timeout = setTimeout(() => {
                close?.();
            }, helperTimeout);

            return () => {
                timeout && clearTimeout(timeout);
            }
        }
    }, [open, uniqueIdentifier, alreadyShown, helperTimeout]);

    if (!open || !elemBounding || alreadyShown) return null;

    const offsetWidth = toolTipRef?.current?.offsetWidth;
    const { style, polygonClassName, polygonStyle } = getTooltipPosition(elemBounding, offsetWidth, forceBelow, customPosition, right, width);

    return (
        <div ref={(ref) => {
            if (!ref || initialized.current) return;

            toolTipRef.current = ref;

            const rect = ref.getBoundingClientRect();

            setRight(rect.right);
            setWidth(rect.width);

            initialized.current = true;
        }}
            className={`${wrapperClassName} fixed z-10`}
            style={style}
            onClick={(e) => e.stopPropagation()}
        >
            <div className={`absolute w-[20px] h-[20px] rotate-[-45deg] bg-primary z-10 ${polygonClassName} ${squareBorderColor} ${squareColor}`} style={polygonStyle || {}} />
            <div className={`${className} max-w-[340px] relative shadow-dashboardTooltip flex flex-row w-full items-start rounded-[15px] rounded-tr-none bg-mint-400 p-[20px] shadow-md`}>

                {children || (
                    <span className="mr-3 font-dmSans text-sm text-black md:text-base">
                        {content}
                    </span>
                )}
                {hasCloseIcon && <img src={CloseIcon} alt="" width={28} height={28} className="cursor-pointer" onClick={close} />}
            </div>
        </div>
    )
};

export default Tooltip;